import React, { useEffect, useState } from 'react';
import {
  TextField,
  DialogActions,
  DialogContent,
  MenuItem,
  Autocomplete,
  Button,
  Box,
  Typography
} from '@mui/material';
import axios from 'axios';

const TradeForm = ({ initialData = {}, onClose, onRefresh }) => {
  const [form, setForm] = useState({
    category: 'stock',
    strategyIds: [],
    symbol: '',
    entryPrice: '',
    exitPrice: '',
    stopLoss: '',
    positionSize: '',
    leverage: 1,
    tradingUrl: initialData?.tradingUrl || '',
    positionType: '',
    tradeDate: '',
    tradeTime: '',
    closeDate: '',
    closeTime: '',
    notes: '',
    tags: '',
    status: 'OPEN',
    ...initialData
  });

  const [strategies, setStrategies] = useState([]);

  useEffect(() => {
    const fetchStrategies = async () => {
      try {
        const token = localStorage.getItem('idToken');
        const res = await axios.get(
          'https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createTradeStrategy',
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStrategies(res.data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchStrategies();
  }, [form.userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const calculateMetrics = () => {
    const { entryPrice, stopLoss, exitPrice, positionSize, leverage } = form;
    const ep = parseFloat(entryPrice);
    const sl = parseFloat(stopLoss);
    const tp = parseFloat(exitPrice);
    const size = parseFloat(positionSize);
    const lev = parseFloat(leverage);
    if (!ep || !sl || !tp || !size || !lev) return {};
    const risk = Math.abs(ep - sl);
    const reward = Math.abs(tp - ep);
    const qty = (size * lev) / ep;
    const riskAmt = risk * qty;
    const rewardAmt = reward * qty;
    return {
      riskAmount: riskAmt.toFixed(2),
      rewardAmount: rewardAmt.toFixed(2),
      quantity: qty.toFixed(2),
      riskRewardRatio: (rewardAmt / riskAmt).toFixed(2)
    };
  };

  const handleSubmit = async () => {
    try {
      const metrics = calculateMetrics();
      const payload = {
        ...form,
        ...metrics,
        rrSummary: metrics.riskRewardRatio ? `R:R = ${metrics.riskRewardRatio} | Risk: $${metrics.riskAmount} | Reward: $${metrics.rewardAmount} | Quantity: ${metrics.quantity}` : '',
        profitLoss: form.status === 'WIN' ? metrics.rewardAmount :
          form.status === 'LOSS' ? `-${metrics.riskAmount}` :
          form.status === 'BREAKEVEN' ? '0' : '',
        pnl: form.status === 'WIN' ? metrics.rewardAmount :
          form.status === 'LOSS' ? `-${metrics.riskAmount}` :
          form.status === 'BREAKEVEN' ? '0' : ''
      };
      const token = localStorage.getItem('idToken');
      await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/trades', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onClose();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error saving trade:', err);
    }
  };

  return (
    <Box
      sx={{
        maxHeight: '80vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        {initialData?.tradeId ? 'Edit Trade' : 'Add Trade'}
      </Typography>

      {/* Trade Identification */}
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>📊 Trade Identification</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <TextField label="Symbol" name="symbol" value={form.symbol} onChange={handleChange} fullWidth />
        <TextField select label="Category" name="category" value={form.category} onChange={handleChange} fullWidth>
          <MenuItem value="stock">Stock</MenuItem>
          <MenuItem value="crypto">Crypto</MenuItem>
          <MenuItem value="forex">Forex</MenuItem>
          <MenuItem value="derivative">Derivative</MenuItem>
          <MenuItem value="commodity">Commodity</MenuItem>
          <MenuItem value="index">Index</MenuItem>
        </TextField>
      </Box>
      <Autocomplete
        multiple
        options={strategies}
        getOptionLabel={(option) => option.name}
        value={strategies.filter((s) => form.strategyIds.includes(s.strategyId))}
        onChange={(event, newValue) => setForm((prev) => ({ ...prev, strategyIds: newValue.map((s) => s.strategyId) }))}
        renderInput={(params) => <TextField {...params} label="Strategies" fullWidth />}
        sx={{ mb: 3 }}
      />

      {/* Position Details */}
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>💰 Position Details</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <TextField select label="Position Type" name="positionType" value={form.positionType} onChange={handleChange} fullWidth>
          <MenuItem value="Long">Long</MenuItem>
          <MenuItem value="Short">Short</MenuItem>
        </TextField>
        <TextField label="Position Size (USD)" name="positionSize" type="number" value={form.positionSize} onChange={handleChange} fullWidth />
        <TextField label="Entry Price" name="entryPrice" type="number" value={form.entryPrice} onChange={handleChange} fullWidth />
        <TextField label="Exit Price" name="exitPrice" type="number" value={form.exitPrice} onChange={handleChange} fullWidth />
        <TextField label="Stop Loss" name="stopLoss" type="number" value={form.stopLoss} onChange={handleChange} fullWidth />
        <TextField label="Leverage" name="leverage" type="number" value={form.leverage} onChange={handleChange} fullWidth inputProps={{ min: 1, max: 100 }} />
      </Box>

      {/* Timing */}
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>⏰ Timing</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <TextField label="Trade Date" name="tradeDate" type="date" value={form.tradeDate} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
        <TextField label="Trade Time" name="tradeTime" type="time" value={form.tradeTime} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
        <TextField label="Close Date" name="closeDate" type="date" value={form.closeDate} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
        <TextField label="Close Time" name="closeTime" type="time" value={form.closeTime} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
      </Box>

      {/* Risk Management */}
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>⚠️ Risk Management</Typography>
      <TextField select label="Status" name="status" value={form.status} onChange={handleChange} fullWidth sx={{ mb: 3 }}>
        <MenuItem value="OPEN">OPEN</MenuItem>
        <MenuItem value="WIN">WIN</MenuItem>
        <MenuItem value="LOSS">LOSS</MenuItem>
        <MenuItem value="BREAKEVEN">BREAKEVEN</MenuItem>
      </TextField>

      {/* Analysis & Learning */}
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>🎯 Analysis & Learning</Typography>
      <Box sx={{ display: 'grid', gap: 2, mb: 3 }}>
        <TextField label="Tags" name="tags" value={form.tags} onChange={handleChange} fullWidth />
        <TextField label="Notes" name="notes" value={form.notes} onChange={handleChange} fullWidth multiline rows={3} />
      </Box>

      {/* References */}
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>🔗 References</Typography>
      <TextField label="Tradingview URL" name="tradingUrl" value={form.tradingUrl || ''} onChange={handleChange} fullWidth sx={{ mb: 3 }} />

      {(() => {
        const metrics = calculateMetrics();
        return metrics.riskRewardRatio ? (
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2">
              R:R = {metrics.riskRewardRatio} | Risk: ${metrics.riskAmount} | Reward: ${metrics.rewardAmount} | Quantity: {metrics.quantity}
            </Typography>
          </Box>
        ) : null;
      })()}

      <TextField
        select
        label="Status"
        name="status"
        value={form.status}
        onChange={handleChange}
        fullWidth
        margin="dense"
      >
        <MenuItem value="OPEN">OPEN</MenuItem>
        <MenuItem value="WIN">WIN</MenuItem>
        <MenuItem value="LOSS">LOSS</MenuItem>
        <MenuItem value="BREAKEVEN">BREAKEVEN</MenuItem>
      </TextField>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">Save</Button>
      </Box>
    </Box>
  );
};

export default TradeForm;
